import React from "react";
import { useQuery } from "@tanstack/react-query";
import LiveNewsFeed from "@/components/LiveNewsFeed";
import api from "@/lib/api";
import { Home as HomeIcon, Building2, TrendingUp, MapPin, ArrowUp, ArrowDown } from "lucide-react";

export default function RealEstate() {
  const { data } = useQuery({
    queryKey: ["realestate-cities"],
    queryFn: async () => (await api.get("/realestate/cities")).data,
  });
  const cities = data?.data || [];

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-1">
        <Building2 className="w-5 h-5 text-emerald-600" />
        <span className="text-xs font-mono-fin uppercase tracking-[0.3em] text-[#D91E2A] font-bold">Property Markets</span>
      </div>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mb-2">Real Estate</h1>
      <p className="text-slate-600 mb-8 max-w-2xl text-sm sm:text-base">Median residential prices across India's top metros — plus live property market news, RERA updates and REIT trends.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <StatBox icon={HomeIcon} label="Residential Sentiment" value="Positive" tone="emerald" note="Q4 2025 sales +12% YoY" />
        <StatBox icon={Building2} label="Commercial Rent (Grade A)" value="+8.4%" tone="emerald" note="Mumbai · Bengaluru leading" />
        <StatBox icon={TrendingUp} label="Listed REITs Yield" value="7.2%" tone="amber" note="Avg. of top 4 Indian REITs" />
      </div>

      <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 mb-4">City Price Index — Median ₹/sqft</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cities.map(c => {
          const up = c.yoy_change_pct >= 0;
          return (
            <div key={c.city} className="rounded-xl bg-white border border-slate-200 p-5 card-glow" data-testid={`realestate-city-${c.city.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D91E2A]" />
                  <span className="font-heading font-bold text-slate-900">{c.city}</span>
                </div>
                <span className={`text-[10px] font-mono-fin uppercase font-semibold px-1.5 py-0.5 rounded ${up ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"}`}>
                  {up ? <ArrowUp className="w-2.5 h-2.5 inline" /> : <ArrowDown className="w-2.5 h-2.5 inline" />} {Math.abs(c.yoy_change_pct)}% YoY
                </span>
              </div>
              <div className="font-mono-fin text-2xl text-slate-900 font-bold">₹{c.median_psf.toLocaleString('en-IN')}</div>
              <div className="text-[10px] font-mono-fin uppercase tracking-wider text-slate-500 mt-0.5">per sq. ft · residential</div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] font-mono-fin uppercase text-slate-500">Hottest Zone</div>
                <div className="text-sm text-slate-800 font-semibold">{c.hot_zone}</div>
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 mb-4">Real Estate News</h2>
      <LiveNewsFeed limit={30} defaultCategory="Real Estate" />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, tone, note }) {
  const toneClass = tone === "emerald" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : "text-slate-900";
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5 card-glow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono-fin uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
        <Icon className="w-4 h-4 text-[#D91E2A]" />
      </div>
      <div className={`font-mono-fin text-2xl font-bold ${toneClass}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{note}</div>
    </div>
  );
}
